import React from "react";
import { Button, Modal, PlusIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { useTranslation } from "react-i18next";

import Wrapper from "~/shared/ui/Wrapper";
import { useGetDnmRoleMappings } from "~/entity/DnmRoleMappings";
import { DnmRoleMappingIProps } from "~/entity/DnmRoleMappings/model/schema";
import {
  useGetLocationData,
  useGoToPreviousPage,
  useSetLocationData,
} from "~/hooks/locationData";
import { OpenModal } from "~/pages/Editor/type";
import { usePermissions } from "~/shared/context/PermissionContext";
import { TableV2 } from "~/shared/ui/TableV2";

import DnmRoleMappingDeleteModal from "./modals/DnmRoleMappingDeleteModal";
import DnmRoleMappingForm from "./modals/DnmRoleMappingForm";
import { getDnmRoleMappingsColumns } from "./model/tableAdapter";
import { DNM_ROLE_MAPPINGS_OPTIONS } from "./utils/options";

const formId = "DnmRoleMappingForm";

const DnmRoleMappings = () => {
  const { t } = useTranslation();
  const goBack = useGoToPreviousPage();
  const setData = useSetLocationData();
  const { hasPermission } = usePermissions();
  const { openModal } = useGetLocationData();
  const { data, isLoading, refetch } = useGetDnmRoleMappings();

  const closeModal = () => setData("openModal", "", "currentId", "");
  const openAddModal = () => setData("openModal", OpenModal.Add);

  const handleRowEdit = (record: DnmRoleMappingIProps) => {
    setData("openModal", OpenModal.Edit, "currentId", record.id);
  };

  const handleRowDelete = (record: DnmRoleMappingIProps) => {
    setData("openModal", OpenModal.Delete, "currentId", record.id);
  };

  return (
    <Wrapper.Content
      title={t("dnmRoleMappings.title")}
      goBackHandler={goBack}
      txtBtn={t("ui.back")}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("dealer.updateDnmRoleMappings") && (
            <Button
              onClick={openAddModal}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("ui.add")}
            </Button>
          )}
        </Flex>
      }
    >
      <TableV2<DnmRoleMappingIProps>
        columns={getDnmRoleMappingsColumns(t, DNM_ROLE_MAPPINGS_OPTIONS)}
        dataSource={data}
        loading={isLoading}
        rowKey="id"
        {...(hasPermission("dealer.updateDnmRoleMappings")
          ? { onRowEdit: handleRowEdit }
          : {})}
        {...(hasPermission("dealer.deleteDnmRoleMappings")
          ? { onRowDelete: handleRowDelete }
          : {})}
      />

      <Modal
        open={openModal === OpenModal.Add || openModal === OpenModal.Edit}
        title={t(
          openModal === OpenModal.Edit
            ? "dnmRoleMappings.title"
            : "dnmRoleMappings.createMapping",
        )}
        onCancel={closeModal}
        closable
        centered
        destroyOnHidden
        okButton={{
          htmlType: "submit",
          form: formId,
          children: t("ui.save"),
        }}
      >
        <DnmRoleMappingForm
          refetch={refetch}
          closeModal={closeModal}
          currentData={data}
          formId={formId}
        />
      </Modal>

      <DnmRoleMappingDeleteModal
        isOpen={openModal === OpenModal.Delete}
        closeModal={closeModal}
        reFetch={refetch}
      />
    </Wrapper.Content>
  );
};

export default DnmRoleMappings;
